/* Pixiwoo — Our Song landing (templates/page.our-song.json): drag-to-reveal cards, canvas rail arrows, mobile sticky bar. No dependencies. */
(() => {
  const MIN_SPLIT = 4;
  const TAP_SPLIT = 15;
  const SWEEP_TO = 25;
  const SWEEP_MS = 1800;

  const mq = (query) => window.matchMedia && window.matchMedia(query).matches;
  const reducedMotion = () => mq('(prefers-reduced-motion: reduce)');
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

  /* ---------- Drag-to-reveal ---------- */
  function initReveal(el) {
    if (el.dataset.pxwOsReady) return;
    el.dataset.pxwOsReady = '1';

    const knob = el.querySelector('[data-pxw-os-knob]');
    let split = 100;
    let drag = null;
    let justDragged = false;
    let lastPointer = 'mouse';

    const set = (value) => {
      split = clamp(value, MIN_SPLIT, 100);
      el.style.setProperty('--split', `${split.toFixed(2)}%`);
      if (knob) knob.setAttribute('aria-valuenow', String(Math.round(split)));
    };
    const touch = () => {
      el.dataset.pxwOsTouched = '1';
      el.classList.add('is-touched');
    };
    const fromX = (clientX) => {
      const r = el.getBoundingClientRect();
      return ((clientX - r.left) / r.width) * 100;
    };
    // Hover-follow only for a real mouse on a hover-capable device
    const hoverMode = (e) => e.pointerType === 'mouse' && mq('(hover: hover) and (pointer: fine)');

    el.addEventListener('pointerdown', (e) => {
      lastPointer = e.pointerType;
      const onKnob = Boolean(knob && knob.contains(e.target));
      if (e.pointerType === 'mouse') {
        if (e.button !== 0) return;
      } else if (!onKnob) {
        return; // touch/pen: only the knob drags, the rest of the image is tap-to-toggle
      }
      drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, horizontal: e.pointerType === 'mouse', moved: false };
      try {
        el.setPointerCapture(e.pointerId);
      } catch (err) {}
      if (drag.horizontal) {
        el.classList.remove('is-leaving');
        el.classList.add('is-dragging');
        touch();
        set(fromX(e.clientX));
      }
    });

    el.addEventListener('pointermove', (e) => {
      if (drag && drag.id === e.pointerId) {
        if (!drag.horizontal) {
          const dx = Math.abs(e.clientX - drag.x0);
          const dy = Math.abs(e.clientY - drag.y0);
          if (dx < 6 && dy < 6) return;
          if (dx <= dy) {
            drag = null; // vertical intent: leave it to the page scroll
            return;
          }
          drag.horizontal = true;
          el.classList.add('is-dragging');
          touch();
        }
        drag.moved = true;
        set(fromX(e.clientX));
        return;
      }
      if (!drag && hoverMode(e)) {
        el.classList.remove('is-leaving');
        touch();
        set(fromX(e.clientX));
      }
    });

    const endDrag = (e) => {
      if (!drag || drag.id !== e.pointerId) return;
      justDragged = drag.moved;
      drag = null;
      el.classList.remove('is-dragging');
      window.setTimeout(() => {
        justDragged = false;
      }, 60);
      if (hoverMode(e) && !el.matches(':hover')) {
        el.classList.add('is-leaving');
        set(100);
      }
    };
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    el.addEventListener('pointerleave', (e) => {
      if (drag || !hoverMode(e)) return;
      el.classList.add('is-leaving');
      set(100);
    });

    // Only block scrolling once a horizontal drag on the knob has been detected
    if (knob) {
      knob.addEventListener(
        'touchmove',
        (e) => {
          if (drag && drag.horizontal && e.cancelable) e.preventDefault();
        },
        { passive: false }
      );
    }

    el.addEventListener('click', () => {
      if (justDragged || lastPointer === 'mouse') return;
      el.classList.remove('is-leaving');
      touch();
      set(split > 50 ? TAP_SPLIT : 100);
    });

    if (knob) {
      knob.addEventListener('keydown', (e) => {
        let next = null;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = split - 10;
        else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = split + 10;
        else if (e.key === 'Home') next = MIN_SPLIT;
        else if (e.key === 'End') next = 100;
        if (next === null) return;
        e.preventDefault();
        el.classList.remove('is-leaving');
        touch();
        set(next);
      });
    }

    el.pxwOsReveal = { set, busy: () => Boolean(drag) || el.dataset.pxwOsTouched === '1' };
    observeSweep(el);
  }

  /* Auto-sweep 100 → 25 → 100 once, the first time a card enters the viewport */
  function sweep(el) {
    if (reducedMotion() || !el.pxwOsReveal) return;
    if (document.hidden) {
      const resume = () => {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', resume);
        sweep(el);
      };
      document.addEventListener('visibilitychange', resume);
      return;
    }
    const api = el.pxwOsReveal;
    if (api.busy()) return;
    el.classList.add('is-sweeping');
    const t0 = performance.now();
    const step = (now) => {
      if (api.busy()) {
        el.classList.remove('is-sweeping');
        return;
      }
      const t = Math.min(1, (now - t0) / SWEEP_MS);
      const p = t < 0.5 ? easeInOut(t / 0.5) : 1 - easeInOut((t - 0.5) / 0.5);
      api.set(100 + (SWEEP_TO - 100) * p);
      if (t < 1) {
        window.requestAnimationFrame(step);
      } else {
        api.set(100);
        el.classList.remove('is-sweeping');
        el.dataset.pxwOsSwept = '1';
      }
    };
    window.requestAnimationFrame(step);
  }

  function observeSweep(el) {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          sweep(el);
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
  }

  /* ---------- Canvas rail arrows ---------- */
  function initRail(root) {
    if (root.dataset.pxwOsReady) return;
    root.dataset.pxwOsReady = '1';
    const track = root.querySelector('[data-pxw-os-rail-track]');
    const prev = root.querySelector('[data-pxw-os-rail-prev]');
    const next = root.querySelector('[data-pxw-os-rail-next]');
    if (!track) return;

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };
    const go = (dir) => {
      track.scrollBy({ left: dir * Math.round(track.clientWidth * 0.8), behavior: reducedMotion() ? 'auto' : 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => go(-1));
    if (next) next.addEventListener('click', () => go(1));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ---------- Mobile sticky bar ---------- */
  let stickyObservers = [];

  function initSticky() {
    stickyObservers.forEach((o) => o.disconnect());
    stickyObservers = [];

    const bar = document.querySelector('[data-pxw-os-sticky-bar]');
    const hero = document.querySelector('[data-pxw-os-hero]');
    const ways = document.getElementById('three-ways');
    if (!bar || !hero || !('IntersectionObserver' in window)) return;

    const link = bar.querySelector('a');
    const state = { heroPast: false, waysIn: false };
    const render = () => {
      const show = state.heroPast && !state.waysIn;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (link) link.tabIndex = show ? 0 : -1;
    };

    const heroIo = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        state.heroPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      });
      render();
    });
    heroIo.observe(hero);
    stickyObservers.push(heroIo);

    if (ways) {
      const waysIo = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          state.waysIn = entry.isIntersecting;
        });
        render();
      });
      waysIo.observe(ways);
      stickyObservers.push(waysIo);
    }
    render();
  }

  function init(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-pxw-os-reveal]').forEach(initReveal);
    root.querySelectorAll('[data-pxw-os-rail]').forEach(initRail);
    initSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor: sections re-render in place
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', () => window.setTimeout(initSticky, 0));
})();
